import { useCallback } from 'react';

import { Card, Divider } from '@/components';
import { IconCaptain } from '@/components/Icons';
import { TshirtTeam } from '@/components/TshirtTeam';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import {
  parseParamsTshirtAndShield,
  tshirtKeys,
} from '@/modules/edit-my-account/functions/utils';
import { initialTshitAndShield } from '@/store/tshirtAndShield';
import { JugadorMiniLigaTO, ResponseVerEquiposML } from '@/types/minileagues';
import { cn } from '@/utils';

export const InfoTeams = ({
  data,
}: {
  data: ResponseVerEquiposML | undefined;
}) => {
  const { isMobile, isDesktop } = useIsDesktop();

  const RowTeam = useCallback(
    ({
      local,
      visitor,
      index,
    }: {
      local: JugadorMiniLigaTO;
      visitor: JugadorMiniLigaTO;
      index: number;
    }) => {
      const SubtitutesTitle = () => (
        <p className="lg:text-left text-center text-gdt-blue font-bold text-sm mt-8 mb-2 lg:text-xs lg:my-4 lg:w-full">
          Suplentes
        </p>
      );
      return (
        <>
          {index === 11 && isMobile && (
            <p className="text-center text-gdt-blue font-bold text-sm mt-8 mb-2 ">
              Suplentes
            </p>
          )}
          <div className="flex w-full justify-between font-bold text-xs text-gdt-gray-2 lg:pl-14 lg:space-y-[15px] lg:items-end lg:before:bg-gdt-gray-5 lg:before:left-1/2 lg:before:top-2 lg:before:h-full lg:before:w-[1px] lg:before:absolute lg:relative lg:h-full">
            <div className="flex-1 lg:flex lg:text-left text-right mt-4 lg:mt-0 pr-5 lg:p-0 lg:flex-wrap">
              {index === 11 && isDesktop && <SubtitutesTitle />}
              <div className="relative inline-block lg:flex lg:order-2">
                {local.capitan && (
                  <IconCaptain className="lg:order-2 bg-gdt-blue text-white absolute -left-6 -top-[2px] lg:-right-6 lg:left-auto" />
                )}
                <p className="lg:order-1 lg:ml-2">
                  {local.nombres} {local.apellido}
                </p>
              </div>
              <div className="font-semibold text-gdt-gray-4 lg:order-1">
                {local.posicion?.nombreCorto}
              </div>
            </div>
            {isMobile && (
              <Divider
                className={cn('w-[1px] bg-gdt-gray-5 h-screen max-h-12', {
                  'top-6 max-h-6 relative': index === 0,
                  'top-2 max-h-10 relative': index === 11,
                })}
                type="vertical"
              />
            )}
            <div className="lg:flex flex-1 text-left lg:mt-0 mt-4 pl-5 lg:pl-14 lg:flex-wrap ">
              {index === 11 && isDesktop && <SubtitutesTitle />}
              <div className="relative inline-block lg:order-2 lg:flex">
                <p className="lg:ml-2">
                  {visitor.nombres} {visitor.apellido}
                </p>
                {visitor.capitan && (
                  <IconCaptain className="bg-gdt-blue text-white absolute -right-6 lg:-right-6 lg:left-auto -top-[2px]" />
                )}
              </div>
              <div className="font-semibold text-gdt-gray-4 lg:order-1">
                {visitor.posicion?.nombreCorto}
              </div>
            </div>
          </div>
        </>
      );
    },
    [isDesktop, isMobile]
  );

  const PrintTeams = useCallback(() => {
    const teams: JSX.Element[] = [];
    if (data && data.equipoPropio && data.equipoAjeno) {
      for (let index = 0; index < data.equipoPropio.length; index++) {
        const local = data.equipoPropio[index];
        const visitor = data.equipoAjeno[index];
        teams.push(
          <RowTeam key={index} index={index} local={local} visitor={visitor} />
        );
      }
    }

    return teams;
  }, [RowTeam, data]);

  const tshirtLocal = parseParamsTshirtAndShield(
    data?.camisetaP,
    tshirtKeys,
    initialTshitAndShield.tshirt
  );

  const tshirtVisitor = parseParamsTshirtAndShield(
    data?.camisetaA,
    tshirtKeys,
    initialTshitAndShield.tshirt
  );

  const MainCard = useCallback(() => {
    return (
      <Card className="flex bg-white w-full justify-between lg:justify-center py-5 font-bold text-xs text-gdt-gray px-0">
        <div className="text-right flex flex-1 lg:justify-end lg:text-left">
          <div className="w-9 min-w-9 mx-4">
            <TshirtTeam
              id={1}
              tshirt={tshirtLocal}
              withShield={
                JSON.stringify(tshirtLocal) ===
                JSON.stringify(initialTshitAndShield.tshirt)
              }
            />
          </div>
          <div>
            <h2>{data?.nUProp}</h2>
            <p className="font-semibold  text-3xs text-gdt-gray-2">
              {data?.nEProp}
            </p>
          </div>
        </div>
        <div className="lg:w-[110px] flex items-center justify-center">
          <span className="flex items-center justify-center h-7 lg:h-9 lg:w-9 lg:p-0 font-bold text-sm text-gdt-status-green bg-gdt-status-green-light rounded-full px-3">
            VS
          </span>
        </div>
        <div className="text-left flex flex-1 lg:justify-start">
          <div>
            <h2>{data?.nUAjeno}</h2>
            <p className="font-semibold text-3xs text-gdt-gray-2">
              {data?.nEAjeno}
            </p>
          </div>
          <div className="w-9 mx-4 min-w-9">
            <TshirtTeam
              id={2}
              tshirt={tshirtVisitor}
              withShield={
                JSON.stringify(tshirtVisitor) ===
                JSON.stringify(initialTshitAndShield.tshirt)
              }
            />
          </div>
        </div>
      </Card>
    );
  }, [
    data?.nEAjeno,
    data?.nEProp,
    data?.nUAjeno,
    data?.nUProp,
    tshirtLocal,
    tshirtVisitor,
  ]);

  return (
    <>
      <MainCard />
      <div className="mt-3 mb-3 lg:mt-0">
        <PrintTeams />
      </div>
    </>
  );
};
